import { DestroyRef, Directive, DOCUMENT, ElementRef, inject, OnInit } from '@angular/core';

@Directive( {
    selector: 'button[hisMultiselectSelectionShowAll]',
    standalone: true
} )
export class HisMultiselectAutocompleteAccessibilityDirective implements OnInit {
    private readonly hostRef = inject<ElementRef<HTMLButtonElement>>( ElementRef );
    private readonly document = inject<Document>( DOCUMENT );
    private readonly destroyRef = inject( DestroyRef );

    ngOnInit() {
        const autocompleteInput = this.getAutocompleteInputElement();
        const overlay = this.getOverlayElement();
        if ( !autocompleteInput || !overlay ) {
            console.warn( 'Could not find autocomplete input or overlay for multiselect accessibility directive.' );
            return;
        }
        
        const handler = ( event: KeyboardEvent ) => {
            if ( event.key === 'ArrowUp' && this.document.activeElement === this.hostRef.nativeElement ) {
                event.preventDefault();
                event.stopPropagation();

                autocompleteInput.focus();
                autocompleteInput.dispatchEvent( new KeyboardEvent( 'keydown', {
                    key: 'ArrowUp',
                    code: 'ArrowUp',
                    bubbles: true,
                    cancelable: true
                } ) );
                return;
            }

            const isLastOptionFocused = this.isFocusedOptionLastInList( overlay );
            if ( event.key === 'ArrowDown' && ( isLastOptionFocused || this.document.activeElement === this.hostRef.nativeElement ) ) {
                if ( isLastOptionFocused ) {
                    this.hostRef.nativeElement.focus();
                }
                event.preventDefault();
                event.stopPropagation();
                return;
            }
        };
        this.document.addEventListener( 'keydown', handler, { capture: true } );

        this.destroyRef.onDestroy( () => {
            this.document.removeEventListener( 'keydown', handler, { capture: true } );
        } );
    }

    private getAutocompleteInputElement(): HTMLInputElement | null {
        const activeElement = this.document.activeElement as HTMLElement;
        if ( activeElement && activeElement.tagName === 'INPUT' && activeElement.classList.contains( 'p-autocomplete-input' ) ) {
            // We assume the autocomplete of the opened dropdown is still focused
            return activeElement as HTMLInputElement;
        }
        // Alternatively, the dropdown ID is stored in the aria-controls attribute of the correct input, so we can find the input element by traversing the DOM
        const overlay = this.getOverlayElement();
        const list = overlay?.querySelector( 'ul.p-autocomplete-list' );
        const inputId = list?.getAttribute( 'id' );
        const inputElement = inputId ? this.document.querySelector<HTMLInputElement>( `input[aria-controls="${inputId}"]` ) : null;
        return inputElement;
    }

    private getOverlayElement(): HTMLElement | null {
        return this.hostRef.nativeElement.closest( '.p-autocomplete-overlay' );
    }

    private getFocusedOptionElement( overlay: HTMLElement ): HTMLLIElement | null {
        return overlay.querySelector( 'li.p-autocomplete-option.p-focus' );
    }

    private isFocusedOptionLastInList( overlay: HTMLElement ): boolean {
        const focusedOption = this.getFocusedOptionElement( overlay );
        return focusedOption ? focusedOption.parentElement.lastElementChild === focusedOption : false;
    }
}